import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { fmt, fmtQty, unitLabel } from '../utils/format';
import Alert from '../components/Alert';
import Pager from '../components/Pager';
import StockModal from '../components/StockModal';
import CategoryFilter from '../components/CategoryFilter';
import { useAuth } from '../context/AuthContext';

export default function Inventory() {
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [category, setCategory] = useState([]);
  const [lowStockThreshold, setLowStockThreshold] = useState(50);
  const [lowStockThresholdLoose, setLowStockThresholdLoose] = useState(5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alert, setAlert] = useState(null);
  const [stockModal, setStockModal] = useState(null);

  useEffect(() => {
    api.get('/settings').then(res => {
      setLowStockThreshold(parseInt(res.data.settings.low_stock_qty) || 50);
      setLowStockThresholdLoose(parseFloat(res.data.settings.low_stock_qty_loose) || 5);
    }).catch(() => {});
  }, []);

  // Debounce search → reset page
  useEffect(() => {
    const t = setTimeout(() => {
      setAppliedSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Fetch on filter / page change
  useEffect(() => {
    setLoading(true);
    const params = { page };
    if (appliedSearch.trim()) params.q = appliedSearch.trim();
    if (category.length) params.category = category.join(',');
    api.get('/products', { params })
      .then(res => {
        setProducts(res.data.products);
        setTotal(res.data.total || 0);
        setPages(res.data.pages || 1);
        if (res.data.categories) setCategories(res.data.categories);
      })
      .catch(() => setError('Could not load products.'))
      .finally(() => setLoading(false));
  }, [appliedSearch, category, page]);

  function handleCategoryChange(newCats) {
    setCategory(newCats);
    setPage(1);
  }

  function stockBadgeClass(qty, isLoose) {
    // Loose stock is in kg / litre — the same units as its threshold.
    const value = parseFloat(qty);
    const threshold = isLoose ? lowStockThresholdLoose : lowStockThreshold;
    if (value < threshold) return 'badge-red';
    if (value < threshold * 4) return 'badge-amber';
    return 'badge-green';
  }

  async function handleStockConfirm(type, quantity, note) {
    try {
      const res = await api.patch(`/products/${stockModal.product.id}/stock`, { type, quantity, note });
      setProducts(prev => prev.map(p => p.id === res.data.product.id ? res.data.product : p));
      setStockModal(null);
      setAlert({ type: 'success', msg: `Stock ${type === 'add' ? 'added' : 'removed'} successfully.` });
    } catch (err) {
      setAlert({ type: 'danger', msg: err?.response?.data?.error || 'Failed to adjust stock.' });
    }
  }

  const hasFilters = searchInput || category.length > 0;

  return (
    <Layout title="Inventory Management">
      <Alert type={alert?.type} message={alert?.msg} onClose={() => setAlert(null)} />
      <Alert type="danger" message={error} onClose={() => setError('')} />

      <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          style={{ maxWidth: 280 }}
          placeholder="Search by name, SKU, barcode…"
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
        />
        <CategoryFilter categories={categories} selected={category} onChange={handleCategoryChange} />
        {hasFilters && (
          <button className="btn" onClick={() => { setSearchInput(''); setAppliedSearch(''); setCategory([]); setPage(1); }}>
            <i className="ti ti-x" aria-hidden="true"></i>Clear
          </button>
        )}
      </div>

      <div className="card">
        {loading ? (
          <div className="empty-state">Loading inventory…</div>
        ) : products.length === 0 ? (
          <div className="empty-state">
            <i className="ti ti-package-off" aria-hidden="true"></i>No products found
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Product Name</th>
                <th>SKU</th>
                <th>Category</th>
                <th>MRP</th>
                <th>Price</th>
                <th>Base Pack</th>
                <th>Stock</th>
                {isAdmin && <th>Adjust</th>}
              </tr>
            </thead>
            <tbody>
              {products.map((p, i) => (
                <tr key={p.id}>
                  <td>{(page - 1) * 20 + i + 1}</td>
                  <td>
                    <strong style={{ fontWeight: 600 }}>{p.name}</strong>
                    <br />
                    <span style={{ fontSize: 10, color: '#9e9e9e' }}>{p.barcode}</span>
                  </td>
                  <td>{p.sku}</td>
                  <td><span className="badge badge-gray">{p.category}</span></td>
                  <td>{fmt(p.mrp)}</td>
                  <td>{fmt(p.price)}</td>
                  <td>
                    {p.is_loose
                      ? <span style={{ fontWeight: 600, color: 'var(--primary, #1976d2)' }}>{unitLabel(p.unit)} (loose)</span>
                      : p.base_pack}
                  </td>
                  <td>
                    <span className={`badge ${stockBadgeClass(p.qty, p.is_loose)}`}>{fmtQty(p.qty, p.unit)}</span>
                  </td>
                  {isAdmin && (
                    <td>
                      <button className="btn btn-sm btn-primary" title="Add stock" onClick={() => setStockModal({ product: p, type: 'add' })}>
                        <i className="ti ti-plus" aria-hidden="true"></i>
                      </button>
                      &nbsp;
                      <button className="btn btn-sm btn-danger" title="Remove stock" onClick={() => setStockModal({ product: p, type: 'minus' })}>
                        <i className="ti ti-minus" aria-hidden="true"></i>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pager page={page} pages={pages} total={total} onPage={setPage} label="products" />

      {isAdmin && stockModal && (
        <StockModal
          product={stockModal.product}
          type={stockModal.type}
          onCancel={() => setStockModal(null)}
          onConfirm={handleStockConfirm}
        />
      )}
    </Layout>
  );
}
